import { useState } from 'react';
import { Package } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePartsCatalog } from '@/hooks/useReferenceData';
import { usePartsMarkup } from '@/hooks/usePartsMarkup';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { netFromGross, type TaxConfig } from '@/utils/tax';
import { effectiveSellPrice, round2 } from '@/utils/partsPricing';

/** The four whitelisted line-item keys shared by estimates and invoices. Never add cost here:
 * line_items jsonb is shipped verbatim to the customer portal and the signature page. */
export interface CatalogLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

interface AddCatalogPartButtonProps {
  /** Tax config of the document — lines are stored ex-tax. */
  tax: TaxConfig;
  onAdd: (item: CatalogLineItem) => void;
}

/** Picker that appends a catalog part to an estimate or invoice at its customer sell price. */
export function AddCatalogPartButton({ tax, onAdd }: AddCatalogPartButtonProps) {
  const { data: catalog = [] } = usePartsCatalog();
  const markupPct = usePartsMarkup();
  const { formatMoney } = useCompanyLocale();
  // Remount the Select after each pick so the placeholder comes back.
  const [pickKey, setPickKey] = useState(0);

  if (catalog.length === 0) return null;

  const sellFor = (part: (typeof catalog)[number]) =>
    effectiveSellPrice(part.default_cost, part.default_sell_price, markupPct);

  const handlePick = (id: string) => {
    const part = catalog.find(p => p.id === id);
    if (!part) return;
    const unit = round2(netFromGross(sellFor(part), tax));
    onAdd({ description: `Parts - ${part.name}`, quantity: 1, unit_price: unit, amount: unit });
    setPickKey(k => k + 1);
  };

  return (
    <Select key={pickKey} onValueChange={handlePick}>
      <SelectTrigger className="w-[260px]" aria-label="Add part from catalog">
        <Package className="h-4 w-4 mr-2 text-muted-foreground" />
        <SelectValue placeholder="Add part from catalog" />
      </SelectTrigger>
      <SelectContent className="bg-popover z-50">
        {catalog.map(part => (
          <SelectItem key={part.id} value={part.id}>
            {part.name} — {formatMoney(sellFor(part))}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
